import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import type { BadgeProps } from '@/shared/ui/badge';
import { BadgeTooltip } from '@/shared/components/badge-tooltip';
import { SelectableCard } from '@/shared/components/selectable-card';
import type { TooltipCopyLine } from '@/shared/components/tooltip-copy-content';

export interface EntityListCardBadge {
  key: string;
  label: string;
  icon?: LucideIcon;
  variant?: BadgeProps['variant'];
  size?: 'sm' | 'md';
  /** Label mais longo (ex.: "Sem cartão") — largura pelo conteúdo. */
  wide?: boolean;
  disabled?: boolean;
  copyLines: TooltipCopyLine[];
  /** Classes extras no wrapper (ex.: `hidden sm:block` para só desktop). */
  className?: string;
  /** Cor customizada (ex.: cor do tier) — sobrepõe o `variant`. */
  style?: import('react').CSSProperties;
}

interface EntityListCardProps {
  selected: boolean;
  onToggle: () => void;
  index: number;
  ariaLabel: string;
  title: string;
  /** Linha secundária abaixo do título (ex.: datas). */
  subtitle?: ReactNode;
  /**
   * Bloco entre identidade e badges (ex.: telefone/e-mail no mobile).
   * Opcional — desktop permanece igual se omitido.
   */
  meta?: ReactNode;
  thumbnail: ReactNode;
  badges: EntityListCardBadge[];
  actions: ReactNode;
  selectable?: boolean;
  /** Clique no corpo — ex.: abrir detalhe (leads). */
  onCardClick?: () => void;
  /**
   * Força badges abaixo do nome/identidade (evita truncar título).
   * Sem isso, em `sm+` as badges ficam ao lado.
   */
  badgesBelow?: boolean;
}

/** Card de listagem padronizado (Leads, Clientes, …). */
export function EntityListCard({
  selected,
  onToggle,
  index,
  ariaLabel,
  title,
  subtitle,
  meta,
  thumbnail,
  badges,
  actions,
  selectable = true,
  onCardClick,
  badgesBelow = false,
}: EntityListCardProps) {
  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      index={index}
      ariaLabel={ariaLabel}
      actions={actions}
      selectable={selectable}
      onCardClick={onCardClick}
    >
      <div
        className={cn(
          'flex min-w-0 items-center gap-3',
          badges.length > 0 &&
            (badgesBelow
              ? 'flex-col items-stretch gap-2.5'
              : 'flex-col items-stretch gap-2.5 sm:flex-row sm:items-center sm:gap-3'),
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {thumbnail}
          <div className="min-w-0 flex-1 overflow-hidden">
            <p
              className={cn(
                'text-sm font-semibold sm:text-base',
                badgesBelow ? 'break-words' : 'truncate',
              )}
            >
              {title}
            </p>
            {subtitle ? <div className="mt-0.5 min-w-0">{subtitle}</div> : null}
            {meta ? <div className="mt-1.5 min-w-0">{meta}</div> : null}
          </div>
        </div>

        {badges.length > 0 ? (
          <div
            className={cn(
              'flex flex-wrap items-center gap-2',
              !badgesBelow && 'sm:justify-end lg:flex-nowrap',
            )}
          >
            {badges.map((badge) => (
              <div key={badge.key} className={badge.className}>
                <BadgeTooltip
                  label={badge.label}
                  icon={badge.icon}
                  variant={badge.variant}
                  size={badge.size}
                  wide={badge.wide}
                  disabled={badge.disabled}
                  copyLines={badge.copyLines}
                  style={badge.style}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SelectableCard>
  );
}
